/**
 * Contract Registry - Centralized contract tracking and error decoding
 *
 * Helps identify which contract/function is causing errors by:
 * - Maintaining a registry of all known contracts with their ABIs
 * - Decoding function selectors from call data
 * - Providing human-readable error messages
 */

import { Interface } from 'ethers';
import { RaffleMiningABI } from '@/abi/RaffleMiningABI';
import { RaffleMiningViewsABI } from '@/abi/RaffleMiningViewsABI';
import { WelephantSwapABI } from '@/abi/WelephantSwapABI';
import { WelephantRewardsABI } from '@/abi/WelephantRewardsABI';
import { StakingVaultABI } from '@/abi/StakingVaultABI';
import { StreamVaultABI } from '@/abi/StreamVaultABI';
import { BNBWelephantSwapABI } from '@/abi/BNBWelephantSwapABI';
import { VanityRegistryABI } from '@/abi/VanityRegistryABI';

// Contract addresses from environment
const CONTRACT_ADDRESS = import.meta.env.VITE_CONTRACT_ADDRESS?.toLowerCase();
const VIEWS_CONTRACT_ADDRESS = import.meta.env.VITE_VIEWS_CONTRACT_ADDRESS?.toLowerCase();
const SWAPPER_ADDRESS = import.meta.env.VITE_SWAPPER_ADDRESS?.toLowerCase();
const STAKING_VAULT_ADDRESS = import.meta.env.VITE_STAKING_VAULT_ADDRESS?.toLowerCase();
const WELEPHANT_ADDRESS = import.meta.env.VITE_WELEPHANT_ADDRESS?.toLowerCase();
const WELEPHANT_REWARDS_ADDRESS = import.meta.env.VITE_WELEPHANT_REWARDS_ADDRESS?.toLowerCase();
const STREAM_VAULT_ADDRESS = import.meta.env.VITE_STREAM_VAULT_ADDRESS?.toLowerCase();
const BNB_WELEPHANT_SWAP_ADDRESS = import.meta.env.VITE_BNB_WELEPHANT_SWAP_ADDRESS?.toLowerCase();
const VANITY_REGISTRY_ADDRESS = import.meta.env.VITE_VANITY_REGISTRY_ADDRESS?.toLowerCase();

// Standard ERC20 ABI for token contracts
const ERC20_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) returns (bool)",
];

// Bridge-related contracts (from bridgeWelephant.ts)
const BRIDGE_CONTRACTS = {
  WITH_REFERRER: "0x412f30e9f8B4a1e99eaE90209A6b00f5C3cc8739".toLowerCase(),
  TBR_BNB: "0x2513515340fF71DD5AF02fC1BdB9615704d91524".toLowerCase(),
  TBR_BASE: "0xD8B736EF27Fc997b1d00F22FE37A58145D3BDA07".toLowerCase(),
  WELEPHANT_BNB: "0x241F21dF529C05289A00dAfeCEa10139A287cDCa".toLowerCase(),
  WELEPHANT_BASE: "0x7d03acf64a1a64bff9d24850011cca93c78a22e7".toLowerCase(),
};

// Contract info type
interface ContractInfo {
  name: string;
  address: string;
  abi: readonly unknown[] | string[];
  network?: string; // 'bnb', 'base', 'local', or undefined for multi-network
}

// Build the registry
const contractRegistry: Map<string, ContractInfo> = new Map();

// Add app contracts (from env)
if (CONTRACT_ADDRESS) {
  contractRegistry.set(CONTRACT_ADDRESS, {
    name: 'RaffleMining',
    address: CONTRACT_ADDRESS,
    abi: RaffleMiningABI,
  });
}

if (VIEWS_CONTRACT_ADDRESS) {
  contractRegistry.set(VIEWS_CONTRACT_ADDRESS, {
    name: 'RaffleMiningViews',
    address: VIEWS_CONTRACT_ADDRESS,
    abi: RaffleMiningViewsABI,
  });
}

if (SWAPPER_ADDRESS) {
  contractRegistry.set(SWAPPER_ADDRESS, {
    name: 'WelephantSwap',
    address: SWAPPER_ADDRESS,
    abi: WelephantSwapABI,
  });
}

if (STAKING_VAULT_ADDRESS) {
  contractRegistry.set(STAKING_VAULT_ADDRESS, {
    name: 'StakingVault',
    address: STAKING_VAULT_ADDRESS,
    abi: StakingVaultABI,
  });
}

if (WELEPHANT_ADDRESS) {
  contractRegistry.set(WELEPHANT_ADDRESS, {
    name: 'WELEPHANT (BNB)',
    address: WELEPHANT_ADDRESS,
    abi: ERC20_ABI,
    network: 'bnb',
  });
}

if (WELEPHANT_REWARDS_ADDRESS) {
  contractRegistry.set(WELEPHANT_REWARDS_ADDRESS, {
    name: 'WelephantRewards',
    address: WELEPHANT_REWARDS_ADDRESS,
    abi: WelephantRewardsABI,
  });
}

if (STREAM_VAULT_ADDRESS) {
  contractRegistry.set(STREAM_VAULT_ADDRESS, {
    name: 'StreamVault',
    address: STREAM_VAULT_ADDRESS,
    abi: StreamVaultABI,
  });
}

if (BNB_WELEPHANT_SWAP_ADDRESS) {
  contractRegistry.set(BNB_WELEPHANT_SWAP_ADDRESS, {
    name: 'BNBWelephantSwap',
    address: BNB_WELEPHANT_SWAP_ADDRESS,
    abi: BNBWelephantSwapABI,
  });
}

if (VANITY_REGISTRY_ADDRESS) {
  contractRegistry.set(VANITY_REGISTRY_ADDRESS, {
    name: 'VanityRegistry',
    address: VANITY_REGISTRY_ADDRESS,
    abi: VanityRegistryABI,
  });
}

// Add bridge contracts
contractRegistry.set(BRIDGE_CONTRACTS.WITH_REFERRER, {
  name: 'TokenBridgeRelayerWithReferrer',
  address: BRIDGE_CONTRACTS.WITH_REFERRER,
  abi: [
    "function transferTokensWithRelay(address tokenBridgeRelayer, address token, uint256 amount, uint16 targetChain, bytes32 targetRecipient, uint32 nonce, bytes32 dstTransferRecipient, bytes32 dstExecutionAddress, uint256 executionAmount, address refundAddr, bytes signedQuoteBytes, bytes relayInstructions, tuple(uint16 dbps, address payee) feeArgs) payable returns (uint64)",
  ],
  network: 'bnb',
});

contractRegistry.set(BRIDGE_CONTRACTS.TBR_BNB, {
  name: 'TokenBridgeRelayer (BNB)',
  address: BRIDGE_CONTRACTS.TBR_BNB,
  abi: [],
  network: 'bnb',
});

contractRegistry.set(BRIDGE_CONTRACTS.TBR_BASE, {
  name: 'TokenBridgeRelayer (Base)',
  address: BRIDGE_CONTRACTS.TBR_BASE,
  abi: [],
  network: 'base',
});

contractRegistry.set(BRIDGE_CONTRACTS.WELEPHANT_BNB, {
  name: 'WELEPHANT (BNB)',
  address: BRIDGE_CONTRACTS.WELEPHANT_BNB,
  abi: ERC20_ABI,
  network: 'bnb',
});

contractRegistry.set(BRIDGE_CONTRACTS.WELEPHANT_BASE, {
  name: 'WELEPHANT (Base)',
  address: BRIDGE_CONTRACTS.WELEPHANT_BASE,
  abi: ERC20_ABI,
  network: 'base',
});

/**
 * Look up a contract by address
 */
export function getContractInfo(address: string): ContractInfo | undefined {
  return contractRegistry.get(address.toLowerCase());
}

/**
 * Get contract name by address, or return shortened address if unknown
 */
export function getContractName(address: string): string {
  const info = getContractInfo(address);
  if (info) return info.name;
  // Return shortened address for unknown contracts
  return `Unknown (${address.slice(0, 6)}...${address.slice(-4)})`;
}

/**
 * Decode function selector from call data
 */
export function decodeFunctionSelector(data: string, address?: string): string | null {
  if (!data || data.length < 10) return null;

  const selector = data.slice(0, 10);

  // If we have an address, try to decode using that contract's ABI
  if (address) {
    const info = getContractInfo(address);
    if (info && info.abi.length > 0) {
      try {
        const iface = new Interface(info.abi as string[]);
        const fragment = iface.getFunction(selector);
        if (fragment) {
          return fragment.name;
        }
      } catch {
        // Fall through to try other ABIs
      }
    }
  }

  // Try all registered ABIs to find the function
  for (const [, info] of contractRegistry) {
    if (info.abi.length === 0) continue;
    try {
      const iface = new Interface(info.abi as string[]);
      const fragment = iface.getFunction(selector);
      if (fragment) {
        return fragment.name;
      }
    } catch {
      // Continue to next ABI
    }
  }

  // Return the raw selector if we can't decode it
  return `selector:${selector}`;
}

/**
 * Decoded function operand (argument)
 */
export interface DecodedOperand {
  name: string;
  type: string;
  value: unknown;
}

/**
 * Error info extracted from an exception
 */
export interface DecodedContractError {
  contractName: string;
  contractAddress: string;
  functionName: string | null;
  operands: DecodedOperand[] | null;
  errorCode: string | null;
  errorReason: string | null;
  rawData: string | null;
  network: string | null;
}

/**
 * Decode function call data to extract operands (arguments)
 */
export function decodeFunctionOperands(data: string, address?: string): { functionName: string; operands: DecodedOperand[] } | null {
  if (!data || data.length < 10) return null;

  const selector = data.slice(0, 10);

  // Try the specific contract's ABI first if address is provided
  const abiSources: ContractInfo[] = [];
  if (address) {
    const info = getContractInfo(address);
    if (info && info.abi.length > 0) {
      abiSources.push(info);
    }
  }

  // Add all other registered ABIs as fallbacks
  for (const [, info] of contractRegistry) {
    if (info.abi.length > 0 && !abiSources.includes(info)) {
      abiSources.push(info);
    }
  }

  for (const info of abiSources) {
    try {
      const iface = new Interface(info.abi as string[]);
      const fragment = iface.getFunction(selector);
      if (fragment) {
        const decoded = iface.decodeFunctionData(fragment, data);
        const operands: DecodedOperand[] = fragment.inputs.map((input, index) => ({
          name: input.name || `arg${index}`,
          type: input.type,
          value: formatOperandValue(decoded[index]),
        }));
        return { functionName: fragment.name, operands };
      }
    } catch {
      // Continue to next ABI
    }
  }

  return null;
}

/**
 * Format an operand value for display
 */
function formatOperandValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  // Handle BigInt values
  if (typeof value === 'bigint') {
    return value.toString();
  }
  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(formatOperandValue);
  }
  // Handle objects with toHexString (ethers Result objects)
  if (typeof value === 'object' && 'toHexString' in value && typeof (value as { toHexString: () => string }).toHexString === 'function') {
    return (value as { toHexString: () => string }).toHexString();
  }
  return value;
}

/**
 * Decode a contract error to provide human-readable information
 */
export function decodeContractError(error: unknown): DecodedContractError | null {
  if (!error || typeof error !== 'object') return null;

  const err = error as {
    code?: string;
    reason?: string;
    message?: string;
    transaction?: {
      to?: string;
      data?: string;
    };
    data?: string;
    info?: {
      error?: {
        data?: string;
      };
    };
  };

  // Extract transaction info
  const txTo = err.transaction?.to;
  const txData = err.transaction?.data;

  if (!txTo) return null;

  const contractInfo = getContractInfo(txTo);

  // Decode function name and operands
  let functionName: string | null = null;
  let operands: DecodedOperand[] | null = null;

  if (txData) {
    const decoded = decodeFunctionOperands(txData, txTo);
    if (decoded) {
      functionName = decoded.functionName;
      operands = decoded.operands;
    } else {
      // Fall back to just the selector if we can't decode operands
      functionName = decodeFunctionSelector(txData, txTo);
    }
  }

  return {
    contractName: contractInfo?.name || getContractName(txTo),
    contractAddress: txTo,
    functionName,
    operands,
    errorCode: err.code || null,
    errorReason: err.reason || null,
    rawData: err.data || err.info?.error?.data || null,
    network: contractInfo?.network || null,
  };
}

/**
 * Format an operand value for display in logs
 */
function formatOperandForLog(value: unknown): string {
  if (value === null || value === undefined) {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(formatOperandForLog).join(', ')}]`;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Format a decoded error for logging
 */
export function formatContractError(decoded: DecodedContractError): string {
  const parts: string[] = [];

  parts.push(`Contract: ${decoded.contractName}`);
  parts.push(`Address: ${decoded.contractAddress}`);

  if (decoded.functionName) {
    parts.push(`Function: ${decoded.functionName}`);
  }

  if (decoded.operands && decoded.operands.length > 0) {
    parts.push('Operands:');
    for (const op of decoded.operands) {
      parts.push(`  ${op.name} (${op.type}): ${formatOperandForLog(op.value)}`);
    }
  }

  if (decoded.errorCode) {
    parts.push(`Code: ${decoded.errorCode}`);
  }

  if (decoded.errorReason) {
    parts.push(`Reason: ${decoded.errorReason}`);
  }

  if (decoded.network) {
    parts.push(`Network: ${decoded.network}`);
  }

  return parts.join('\n');
}

/**
 * Log a contract error with decoded information
 */
export function logContractError(context: string, error: unknown): void {
  console.error(`[${context}] Contract error:`, error);

  const decoded = decodeContractError(error);
  if (decoded) {
    console.error(`[${context}] Decoded error:\n${formatContractError(decoded)}`);
  }
}

/**
 * List all registered contracts (useful for debugging)
 */
export function listRegisteredContracts(): ContractInfo[] {
  return Array.from(contractRegistry.values());
}

/**
 * Check if an address is a known contract
 */
export function isKnownContract(address: string): boolean {
  return contractRegistry.has(address.toLowerCase());
}

// Export for use in debugging
export { contractRegistry };
